import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { Button, IconButton, type ButtonKind } from './Button.tsx'
import { Textarea } from './controls.tsx'
import { cx } from './cx.ts'
import { popModal, pushModal } from './layers.ts'

export interface DialogProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Bottom sheet on phones. */
  sheet?: boolean
}

/** Modal dialog built on <dialog> (focus trap and Escape handled by the browser). */
export function Dialog({ open, onClose, title, children, footer, size = 'md', sheet = true }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null)
  const slot = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal()
    if (!open && d.open) d.close()
    if (!open || !slot.current) return
    // Registered while open (and until unmount) so toasts render inside the top modal.
    pushModal({ dialog: d, slot: slot.current })
    return () => popModal(d)
  }, [open])
  const widths = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' }
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault()
        onClose()
      }}
      className={cx(
        'm-0 w-full max-w-none bg-transparent p-0 backdrop:bg-ink/55 sm:m-auto',
        sheet ? 'mt-auto sm:mt-auto' : 'm-auto',
        widths[size],
      )}
    >
      {open && (
        <div
          className={cx(
            'flex max-h-[92dvh] flex-col bg-white shadow-[var(--shadow-pop)]',
            sheet ? 'rounded-t-2xl sm:mb-[8vh] sm:rounded-2xl' : 'rounded-2xl',
          )}
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3">
            <h2 id={titleId} className="display text-[22px]">
              {title}
            </h2>
            <IconButton label="Close" onClick={onClose}>
              <X className="size-5" />
            </IconButton>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">{footer}</div>}
        </div>
      )}
      <div ref={slot} />
    </dialog>
  )
}

interface ConfirmOptions {
  title: string
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  kind?: ButtonKind
  /** Ask for a reason; the promise resolves with it. */
  reason?: { label: string; placeholder?: string; minLength?: number }
}

type ConfirmResult = { ok: false } | { ok: true; reason: string }

const ConfirmCtx = createContext<((o: ConfirmOptions) => Promise<ConfirmResult>) | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (r: ConfirmResult) => void }) | null>(null)
  const [reason, setReason] = useState('')
  const confirm = useCallback(
    (o: ConfirmOptions) =>
      new Promise<ConfirmResult>((resolve) => {
        setReason('')
        setState({ ...o, resolve })
      }),
    [],
  )
  const close = (r: ConfirmResult) => {
    state?.resolve(r)
    setState(null)
  }
  const min = state?.reason?.minLength ?? 3
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Dialog
        open={!!state}
        onClose={() => close({ ok: false })}
        title={state?.title ?? ''}
        size="sm"
        footer={
          <>
            <Button kind="ghost" onClick={() => close({ ok: false })}>
              {state?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              kind={state?.kind ?? 'forest'}
              disabled={!!state?.reason && reason.trim().length < min}
              onClick={() => close({ ok: true, reason: reason.trim() })}
            >
              {state?.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-[14px] leading-relaxed">
          {state?.body}
          {state?.reason && (
            <label className="flex flex-col gap-1.5">
              <span className="label-caps">{state.reason.label}</span>
              <Textarea rows={2} value={reason} placeholder={state.reason.placeholder} onChange={(e) => setReason(e.target.value)} />
            </label>
          )}
        </div>
      </Dialog>
    </ConfirmCtx.Provider>
  )
}

export function useConfirm() {
  const v = useContext(ConfirmCtx)
  if (!v) throw new Error('useConfirm outside ConfirmProvider')
  return v
}
